import { BadgeList } from '@components/shared/badge-list.component';
import { Tile } from '@components/shared/tile.component';
import { useGetTechnology } from '@queries/technology.query';
import { TechnologyHeaderTab } from '@utils/query-state.util';
import { pluralize } from '@utils/text.util';
import { ArrowRightIcon, BoxIcon, LayersIcon } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';

type UsesSubsystemsCompactProps = {
  onNavigateToDetails?: (tab: TechnologyHeaderTab) => void;
};

export const UsesSubsystemsCompact: FC<UsesSubsystemsCompactProps> = (props) => {
  const { onNavigateToDetails } = props;
  const { technologyId } = useParams();
  const { data: technology, isPending: isTechnologyLoading } = useGetTechnology(technologyId);

  const sortedUses = useMemo(() => {
    const items = technology?.uses ?? [];
    return [...items].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [technology?.uses]);

  const sortedSubsystems = useMemo(() => {
    const items = technology?.subsystems ?? [];
    return [...items].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [technology?.subsystems]);

  const usesCount = sortedUses.length;
  const subsystemsCount = sortedSubsystems.length;
  const canNavigate = Boolean(onNavigateToDetails);

  const handleNavigate = () => {
    onNavigateToDetails?.('details');
  };

  return (
    <Tile
      className="min-h-fit"
      header={{
        iconButton: canNavigate
          ? {
              icon: <ArrowRightIcon size={16} />,
              onClick: handleNavigate,
            }
          : undefined,
        title: 'Uses & Subsystems',
      }}
      hoverReveal={canNavigate}
      isLoading={isTechnologyLoading}
    >
      <div className="flex flex-col gap-4">
        {/* Uses Section */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <BoxIcon
              className="text-gray-400"
              size={16}
            />
            <span className="text-sm font-medium text-gray-900">{pluralize(usesCount, 'Use')}</span>
          </div>
          {usesCount > 0 && (
            <div className="pl-6">
              <BadgeList
                items={sortedUses}
                limit={6}
                size="xs"
              />
            </div>
          )}
        </div>

        {/* Subsystems Section */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <LayersIcon
              className="text-gray-400"
              size={16}
            />
            <span className="text-sm font-medium text-gray-900">{pluralize(subsystemsCount, 'Subsystem')}</span>
          </div>
          {subsystemsCount > 0 && (
            <div className="pl-6">
              <BadgeList
                items={sortedSubsystems}
                limit={6}
                size="xs"
              />
            </div>
          )}
        </div>
      </div>
    </Tile>
  );
};
